@file:OptIn(ExperimentalResourceApi::class)

package ui.server

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import api.CurrencyType
import common.frontend.api.Io
import org.jetbrains.compose.resources.ExperimentalResourceApi
import provider.LocalAccountProvider
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.viewmodel.globalViewModel
import tk.mallumo.compose.navigation.viewmodel.viewModel
import tools.EFieldList
import tools.EFieldTextString
import ui.GlobalVM
import ui.widget.ListingSpecs


interface ServerListingScope {

    val vmGlobal: GlobalVM
    val vm: ServerListingVM
    val action: (ServerListingVM.Action) -> Unit
    val nav: Navigation
    val login: Io.User

    companion object {
        val headerSpecs = ListingSpecs.create<Wrapper.Item>(
            ListingSpecs.Item(title = Res.string.server_listing__item_id, width = 50.dp, align = TextAlign.Center) { ListingSpecs.trimmedId(it.data.id) },
            ListingSpecs.Item(title = Res.string.server_listing__item_name, width = 200.dp) { it.data.name },
            ListingSpecs.Item(title = Res.string.server_listing__item_companies, width = 80.dp) { it.data.count_company },
            ListingSpecs.Item(title = Res.string.server_listing__item_locations, width = 80.dp) { it.data.count_location },
            ListingSpecs.Item(title = Res.string.server_listing__item_users, width = 80.dp) { it.data.count_user },
            ListingSpecs.Item(title = Res.string.server_listing__item_devices, width = 80.dp) { it.data.count_device },
            ListingSpecs.Item(
                title = Res.string.server_listing__item_fee_license,
                align = TextAlign.End,
                width = 90.dp
            ) {
                if (it.data.license.sum == 0) "- ${CurrencyType[it.data.license.currency].symbol}"
                else "${it.data.license.sum} ${CurrencyType[it.data.license.currency].symbol}"
            },
            ListingSpecs.Item(
                title = Res.string.server_listing__item_fee_total,
                align = TextAlign.End,
                width = 90.dp
            ) { entry ->
                buildString {
                    entry.data.license.sum.toDouble()
                        .takeIf { entry.data.license.sum > 0 }
                        ?.let { it * (entry.data.license.percentage.toDouble() / 100.0) }
                        ?.asDynamic()
                        ?.toFixed(2)
                        ?.toString()
                        ?.also {
                            append(it)
                        } ?: run {
                        append("-")
                    }
                    append(" ")
                    append(CurrencyType[entry.data.license.currency].symbol)
                }
            }
        )


        @Composable
        fun rememberScope(): ServerListingScope {
            val account = LocalAccountProvider.current
            val nav = LocalNavigation.current
            val vm = nav.viewModel(ServerListingVM::class)
            val vmGlobal = globalViewModel(GlobalVM::class)
            val action = ServerListingVM.Action.rememberAction(vm)

            return remember {
                object : ServerListingScope {
                    override val vmGlobal: GlobalVM
                        get() = vmGlobal
                    override val vm: ServerListingVM
                        get() = vm
                    override val action: (ServerListingVM.Action) -> Unit
                        get() = action
                    override val nav: Navigation
                        get() = nav
                    override val login: Io.User
                        get() = account.current!!

                }.also {
                    vm.init(it)
                }
            }
        }
    }

    class Wrapper(listing: Io.ListingServer) : EFieldList<Io.ListingServer.Item, Wrapper.Item>(
        items = listing.items,
        onBindEntry = { Item(it) }
    ) {
        class Item(
            override val data: Io.ListingServer.Item,
            override val key: String = data.id.toString(),
        ) : Entry<Io.ListingServer.Item> {

            val name: EFieldTextString = EFieldTextString(
                property = data::name,
                condition = { it.trim().takeIf { it.length > 4 } })

        }
    }
}
